import { IDropdownOption } from "@fluentui/react";
import type { FormEvent, SyntheticEvent } from "react";
import { useCallback } from "react";
import { ChatModel, RetrievalMode, ReasoningEffort, saveUserSettings, getReasoningEffortsForModel } from "../api";
import { userInfo } from "../authConfig";

interface UseSettingsHandlersProps {
    setRetrieveCount: (value: number) => void;
    setChatModel: (value: ChatModel) => void;
    setOnlyCitations: (value: boolean) => void;
    setShouldStream: (value: boolean) => void;
    setUseSuggestFollowupQuestions: (value: boolean) => void;
    setRetrievalMode: (value: RetrievalMode) => void;
    setUseSemanticRanker: (value: boolean) => void;
    setUseSemanticCaptions: (value: boolean) => void;
    setExcludeCategory: (value: string) => void;
    onReasoningEffortChange?: (value: ReasoningEffort) => void;
    reasoningEffort?: ReasoningEffort;
}

/**
 * Custom hook that provides reusable settings change handlers.
 * Used by both Chat.tsx and OneShot.tsx to manage search and AI settings.
 */
export const useSettingsHandlers = (props: UseSettingsHandlersProps) => {
    const {
        setRetrieveCount,
        setChatModel,
        setOnlyCitations,
        setShouldStream,
        setUseSuggestFollowupQuestions,
        setRetrievalMode,
        setUseSemanticRanker,
        setUseSemanticCaptions,
        setExcludeCategory,
        onReasoningEffortChange: setReasoningEffort,
        reasoningEffort
    } = props;

    const onRetrieveCountChange = useCallback(
        (_ev?: SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
            setRetrieveCount(parseInt(newValue ?? "3"));
        },
        [setRetrieveCount]
    );

    const onChatModelChange = useCallback(
        (_ev?: FormEvent<HTMLDivElement>, option?: IDropdownOption<ChatModel> | undefined) => {
            const model = option?.data ?? ChatModel.GPT_41_MINI;
            setChatModel(model);
            saveUserSettings({ aimodel: model });
            userInfo.aimodel = model;

            // Reset reasoning effort if current value is not valid for the new model
            if (setReasoningEffort && reasoningEffort) {
                const validEfforts = getReasoningEffortsForModel(model);
                if (validEfforts.length > 0 && !validEfforts.includes(reasoningEffort)) {
                    setReasoningEffort(ReasoningEffort.Low);
                }
            }
        },
        [setChatModel, setReasoningEffort, reasoningEffort]
    );

    const onOnlyCitationsChange = useCallback(
        (_ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            setOnlyCitations(!!checked);
            setShouldStream(!checked);
            setUseSuggestFollowupQuestions(false);
        },
        [setOnlyCitations, setShouldStream, setUseSuggestFollowupQuestions]
    );

    const onRetrievalModeChange = useCallback(
        (_ev: FormEvent<HTMLDivElement>, option?: IDropdownOption<RetrievalMode> | undefined) => {
            setRetrievalMode(option?.data ?? RetrievalMode.Hybrid);
        },
        [setRetrievalMode]
    );

    const onUseSemanticRankerChange = useCallback(
        (_ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            setUseSemanticRanker(!!checked);
        },
        [setUseSemanticRanker]
    );

    const onUseSemanticCaptionsChange = useCallback(
        (_ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            setUseSemanticCaptions(!!checked);
        },
        [setUseSemanticCaptions]
    );

    const onShouldStreamChange = useCallback(
        (_ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            setShouldStream(!!checked);
        },
        [setShouldStream]
    );

    const onExcludeCategoryChanged = useCallback(
        (_ev?: FormEvent, newValue?: string) => {
            setExcludeCategory(newValue ?? "");
        },
        [setExcludeCategory]
    );

    const onUseSuggestFollowupQuestionsChange = useCallback(
        (_ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
            setUseSuggestFollowupQuestions(!!checked);
        },
        [setUseSuggestFollowupQuestions]
    );

    const onReasoningEffortChange = useCallback(
        (_ev?: FormEvent<HTMLDivElement>, option?: IDropdownOption<ReasoningEffort> | undefined) => {
            if (setReasoningEffort) {
                setReasoningEffort(option?.data ?? ReasoningEffort.Low);
            }
        },
        [setReasoningEffort]
    );

    return {
        onRetrieveCountChange,
        onChatModelChange,
        onOnlyCitationsChange,
        onRetrievalModeChange,
        onUseSemanticRankerChange,
        onUseSemanticCaptionsChange,
        onShouldStreamChange,
        onExcludeCategoryChanged,
        onUseSuggestFollowupQuestionsChange,
        onReasoningEffortChange
    };
};
